'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

// ── Global subscription-based state ──────────────────────────────
// Allows any module (including non-React code like axios interceptors)
// to open the quota dialog without a React context.

type Listener = (open: boolean) => void
let _listeners: Listener[] = []
let _isOpen = false

export function subscribeToQuotaDialog(listener: Listener) {
  _listeners.push(listener)
  return () => {
    _listeners = _listeners.filter((l) => l !== listener)
  }
}

export function showQuotaDialog() {
  if (_isOpen) return
  _isOpen = true
  _listeners.forEach((l) => l(true))
}

export function closeQuotaDialog() {
  _isOpen = false
  _listeners.forEach((l) => l(false))
}

/**
 * QuotaDialog — a centered modal that appears when the user's account
 * balance is insufficient.  It does NOT auto-close; the user must
 * explicitly dismiss it or navigate to the top-up page.
 */
export function QuotaDialog() {
  const [open, setOpen] = useState(false)
  const router = useRouter()

  useEffect(() => {
    return subscribeToQuotaDialog((isOpen) => setOpen(isOpen))
  }, [])

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) closeQuotaDialog()
      }}
    >
      <DialogContent
        title="余额不足"
        closeOnOutsideClick={false}
        showCloseButton={false}
        className="sm:max-w-md py-8"
      >
        <DialogTitle className="text-xl mb-4">余额不足</DialogTitle>
        <DialogDescription className="text-base leading-relaxed">
          账户余额不足，部分功能可能受限，请充值后继续使用。
        </DialogDescription>
        <div className="mt-8 flex items-center justify-center gap-4">
          <Button
            size="lg"
            className="min-w-[120px]"
            onClick={() => {
              closeQuotaDialog()
              router.push('/member/top-up')
            }}
          >
            去充值
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="min-w-[120px]"
            onClick={() => closeQuotaDialog()}
          >
            我知道了
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
